import React, { useState, useEffect, useCallback } from 'react';
import { VolumeDiscount, VolumeDiscountCreate, VolumeDiscountUpdate } from '../types/costwand.d';
import { Input } from './ui/input.tsx';
import { Button } from './ui/button.tsx';
import { Label } from '@radix-ui/react-label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select.tsx';
import { useProductAndCompositeLookup } from '../hooks/useProductAndCompositeLookup.ts';
import { Spinner } from './ui/spinner.tsx'; // Import Spinner

interface VolumeDiscountFormProps {
  initialData?: VolumeDiscount | null;
  onSubmit: (data: VolumeDiscountCreate | VolumeDiscountUpdate) => void;
  onCancel: () => void;
  isSubmitting?: boolean;
  // error?: string | null; // Removed error prop
}

const VolumeDiscountForm: React.FC<VolumeDiscountFormProps> = ({
  initialData,
  onSubmit,
  onCancel,
  isSubmitting = false,
  // error, // Removed error from destructuring
}) => {
  const { lookupItems, loading: lookupLoading, error: lookupError } = useProductAndCompositeLookup();

  const [formData, setFormData] = useState<VolumeDiscountCreate | VolumeDiscountUpdate>(() => {
    const defaultFormData = {
      min_quantity: 0,
      price_retained_perc: 0.0,
    };

    if (initialData) {
      return {
        product_id: initialData.product_id,
        min_quantity: initialData.min_quantity,
        price_retained_perc: initialData.price_retained_perc,
        updated_by: 'tester',
      };
    } else {
      return {
        product_id: null as any, // Changed from 0 to null for new creation
        ...defaultFormData,
        created_by: 'tester',
      };
    }
  });

  useEffect(() => {
    if (initialData) {
      setFormData({
        product_id: initialData.product_id || null, // Ensure null if 0 or undefined
        min_quantity: initialData.min_quantity,
        price_retained_perc: initialData.price_retained_perc,
        updated_by: initialData.updated_by || 'tester',
      });
    } else {
      setFormData({
        product_id: null as any, // Ensure null for new creation
        min_quantity: 0,
        price_retained_perc: 0.0,
        created_by: 'tester',
      });
    }
  }, [initialData]);

  const handleChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const { id, value } = e.target;
    setFormData((prev) => {
      const parsedValue = id === 'price_retained_perc' ? parseFloat(value) || 0.0 : parseInt(value, 10) || 0;
      return {
        ...prev,
        [id]: parsedValue,
      };
    });
  }, []);

  const handleProductSelectChange = useCallback((value: string) => {
    setFormData((prev) => ({
      ...(prev as VolumeDiscountCreate),
      product_id: value === '' ? null : parseInt(value, 10), // Handle empty string for 'None'
    }));
  }, []);

  const handleSubmit = useCallback((e: React.FormEvent) => {
    e.preventDefault();
    onSubmit(formData);
  }, [formData, onSubmit]);

  const currentProductId = initialData?.product_id || (formData as VolumeDiscountCreate).product_id;
  // const selectedProductDescription = lookupItems.find(item => item.id === currentProductId)?.description; // Not used

  // isValid check needs to consider null for product_id
  const isValid = !isSubmitting && !lookupLoading && (currentProductId !== null && currentProductId !== 0 || initialData);

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {/* {error && <p className="text-destructive text-sm">{error}</p>} */} {/* Removed local error display */}
      {lookupError && <p className="text-destructive text-sm">Error loading products/composites: {lookupError}</p>}

      <div>
        <Label htmlFor="product_id" className="block text-sm font-medium text-foreground mb-1">Product/Composite</Label>
        {initialData ? (
          <Input
            id="product_id_display"
            type="text"
            value={initialData._labels?.product_id || `ID: ${initialData.product_id}`}
            disabled
            className="bg-muted text-muted-foreground"
          />
        ) : (
          <Select
            onValueChange={handleProductSelectChange}
            value={currentProductId ? String(currentProductId) : ''} // Ensure it's '' if null/0
            disabled={lookupLoading || isSubmitting}
            required
          >
            <SelectTrigger id="product_id">
              <SelectValue placeholder={lookupLoading ? <span className="flex items-center gap-2"><Spinner size="sm" /> Loading products...</span> : "Select a product or composite"} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="nil" disabled> {/* Added for required field placeholder */}
                {lookupLoading ? <span className="flex items-center gap-2"><Spinner size="sm" /> Loading products...</span> : "Select a product or composite"}
              </SelectItem>
              {lookupItems.map((item) => (
                <SelectItem key={item.id} value={String(item.id)}>
                  {item.description}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
      </div>

      <div>
        <Label htmlFor="min_quantity" className="block text-sm font-medium text-foreground mb-1">Minimum Quantity</Label>
        <Input
          id="min_quantity"
          type="number"
          required
          value={String(formData.min_quantity)}
          onChange={handleChange}
          disabled={isSubmitting}
        />
      </div>
      <div>
        <Label htmlFor="price_retained_perc" className="block text-sm font-medium text-foreground mb-1">Price Retained Percentage</Label>
        <Input
          id="price_retained_perc"
          type="number"
          step="0.01"
          required
          value={String(formData.price_retained_perc)}
          onChange={handleChange}
          disabled={isSubmitting}
        />
      </div>
      <div className="flex justify-end gap-2">
        <Button type="submit" disabled={!isValid}>
          {isSubmitting ? <span className="flex items-center gap-2"><Spinner size="sm" /> Saving...</span> : 'Save'}
        </Button>
        <Button type="button" variant="outline" onClick={onCancel} disabled={isSubmitting}>
          Cancel
        </Button>
      </div>
    </form>
  );
};

export default VolumeDiscountForm;